// src/sectors/pilates/reports/staff/tabs/SessionsTab.jsx
import React, { useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import MDBox from 'components/MDBox';

import { endOfMonth, startOfMonth, toISODateInput } from '../utils/staffReportFormatters';
import { StaffFiltersCard } from '../components/StaffFiltersCard';
import { StaffSessionsTable } from '../components/StaffSessionsTable';
import { useTrainerSessionsReport } from '../hooks/useTrainerSessionsReport';
import { StaffSessionsAccordion } from '../components/StaffSessionsAccordion';

// ✅ NEW
import { StaffMembersAccordion } from '../components/StaffMembersAccordion';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MDInput from 'components/MDInput';
import { Icon, InputAdornment } from '@mui/material';

function pickStaffId(s) {
  return s?.staffId ?? s?.id ?? null;
}

export default function SessionsTab() {
  const [from, setFrom] = useState(startOfMonth(new Date()));
  const [to, setTo] = useState(endOfMonth(new Date()));
  const [selectedStaff, setSelectedStaff] = useState(null);

  // ✅ 3 mod
  const [viewMode, setViewMode] = useState('PARTICIPANTS');
  // 'PARTICIPANTS' | 'SESSIONS' | 'MEMBERS'

  const trainerId = pickStaffId(selectedStaff);

  const fromStr = toISODateInput(from);
  const toStr = toISODateInput(to);

  const q = useTrainerSessionsReport({
    trainerId,
    from: fromStr,
    to: toStr,
    limit: 200,
  });

  const rows = useMemo(() => (Array.isArray(q.data) ? q.data : []), [q.data]);
  const [qText, setQText] = useState('');

  const filteredRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const q = (qText || '').trim().toLowerCase();
    if (!q) return list;

    return list.filter((r) => {
      const hay = [
        r?.memberName,
        r?.activityName,
        r?.sessionType,
        r?.packageName,
        r?.packagePeriod,
        r?.note,
        r?.attendanceTakenByName,
        r?.paymentMethod,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, qText]);

  return (
    <MDBox>
      <StaffFiltersCard
        from={from}
        to={to}
        selectedStaff={selectedStaff}
        onSelectStaff={setSelectedStaff}
        loading={q.isLoading}
        onChangeFrom={setFrom}
        onChangeTo={setTo}
        onList={() => q.refetch()}
      />

      <MDBox mt={1.2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        {!trainerId ? (
          <Chip label="Eğitmen seç" variant="outlined" />
        ) : q.isError ? (
          <Chip label="⚠️ Rapor çekilemedi" color="error" variant="outlined" />
        ) : (
          <Chip
            label={q.isFetching ? 'Yükleniyor…' : 'Veriler hazır'}
            color={q.isFetching ? 'default' : 'success'}
            variant="outlined"
          />
        )}

        <Chip label={`${filteredRows.length} satır`} variant="outlined" />

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(e, val) => {
            if (val) setViewMode(val);
          }}
          sx={{
            height: 32,
            bgcolor: 'grey.100',
            borderRadius: 2,
            '& .MuiToggleButton-root': {
              px: 1.5,
              fontSize: 11.5,
              textTransform: 'none',
              borderRadius: 2,
            },
          }}
        >
          <ToggleButton value="PARTICIPANTS">Katılımcı Bazlı</ToggleButton>
          <ToggleButton value="SESSIONS">Seans Bazlı</ToggleButton>
          <ToggleButton value="MEMBERS">Üye Bazlı</ToggleButton>
        </ToggleButtonGroup>

        <MDBox sx={{ minWidth: 260, flex: '1 1 260px' }}>
          <MDInput
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Ara: üye, aktivite, paket, not…"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small">search</Icon>
                </InputAdornment>
              ),
            }}
          />
        </MDBox>
      </MDBox>

      <MDBox mt={1}>
        {viewMode === 'PARTICIPANTS' ? (
          <StaffSessionsTable rows={filteredRows} limit={30} />
        ) : viewMode === 'SESSIONS' ? (
          <StaffSessionsAccordion rows={filteredRows} limit={30} />
        ) : (
          <StaffMembersAccordion rows={filteredRows} limit={30} />
        )}
      </MDBox>
    </MDBox>
  );
}
